/**
 * Report parameters: mapping the metadata records onto something renderable,
 * and the XML the parameter values are stored as on a saved filter.
 */

import { encodeXml, parseRecordSource, readElement } from "./recordSource.js";
import type {
	ReportParameter,
	ReportParameterEditor,
	ReportParameterRecord,
	ReportParameterType,
	ReportParameterValue,
	ReportParameterValues,
} from "./types.js";

const PARAMETER_TYPES: ReportParameterType[] = ["String", "Integer", "Double", "Date", "Boolean"];

/**
 * A default value naming a stored procedure, e.g. `astp_Reports_CurrentPeriod`.
 *
 * The Windows launcher executes the procedure and uses the first column of the
 * first row. The web client cannot execute an arbitrary procedure, so these are
 * resolved through the report endpoint instead.
 */
const PROCEDURE_DEFAULT = /^[a-z]stp_/i;

/** True when a `DefaultValue` names a stored procedure rather than a literal. */
export function isProcedureDefault(defaultValue: string | null | undefined): boolean {
	return typeof defaultValue === "string" && PROCEDURE_DEFAULT.test(defaultValue.trim());
}

/** The choices of a parameter, one per line of its `Items` column. */
export function parseParameterItems(items: string | null | undefined): string[] {
	if (!items) return [];

	return items
		.split(/\r\n|\r|\n/)
		.map((item) => item.trim())
		.filter((item) => item !== "");
}

function toParameterType(dataType: string | null | undefined): ReportParameterType {
	let match = PARAMETER_TYPES.find((type) => type.toLowerCase() === dataType?.trim().toLowerCase());

	// The designer only offers the five known types, but an unknown one is still
	// editable as text.
	return match ?? "String";
}

/**
 * The editor a parameter should use. A lookup or a fixed item list wins over the
 * data type, which is what the Windows launcher does as well.
 */
function toParameterEditor(type: ReportParameterType, hasLookup: boolean, hasItems: boolean): ReportParameterEditor {
	if (hasLookup) return "lookup";
	if (hasItems) return "select";

	switch (type) {
		case "Integer":
		case "Double":
			return "number";
		case "Date":
			return "date";
		case "Boolean":
			return "boolean";
		default:
			return "text";
	}
}

/** Maps a `sviw_WinClient_ReportsParameters` record onto a `ReportParameter`. */
export function toReportParameter(record: ReportParameterRecord): ReportParameter {
	let name = record.Name ?? "";
	let type = toParameterType(record.DataType);
	let items = parseParameterItems(record.Items);
	let recordSource = parseRecordSource(record.RecordSource);
	let valueMember = record.ValueMember ?? null;

	if (recordSource && !valueMember) {
		valueMember = recordSource.columns[0]?.name ?? null;
	}

	/*
	 * The designer has no display member: the Windows lookup grid shows every
	 * selected column. On the web a combobox shows one, so the first column that
	 * is not the value is used.
	 */
	let displayMember = recordSource?.columns.find((column) => column.name !== valueMember)?.name ?? null;
	let hasLookup = Boolean(recordSource && valueMember);

	return {
		name,
		label: record.Label?.trim() || name,
		description: record.Description?.trim() || null,
		type,
		editor: toParameterEditor(type, hasLookup, items.length > 0),
		defaultValue: record.DefaultValue ?? null,
		items,
		recordSource: hasLookup ? recordSource : null,
		valueMember: hasLookup ? valueMember : null,
		displayMember: hasLookup ? displayMember : null,
		sortOrder: record.SortOrder ?? 0,
		lookupWidth: record.LookupWidth ?? null,
		lookupHeight: record.LookupHeight ?? null,
	};
}

/** Maps and sorts a report's parameter records. */
export function toReportParameters(records: ReportParameterRecord[]): ReportParameter[] {
	return records
		.filter((record) => record.Name)
		.map(toReportParameter)
		.sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label));
}

/** Converts a raw value to the type a parameter expects. */
export function coerceParameterValue(
	parameter: ReportParameter,
	value: ReportParameterValue | undefined,
): ReportParameterValue {
	if (value === null || value === undefined || value === "") {
		return parameter.type === "Boolean" ? false : null;
	}

	switch (parameter.type) {
		case "Boolean":
			return typeof value === "boolean" ? value : /^(true|1|ja|yes)$/i.test(String(value));
		case "Integer": {
			let parsed = Number.parseInt(String(value), 10);

			return Number.isNaN(parsed) ? null : parsed;
		}
		case "Double": {
			// Norwegian keyboards produce a comma, which `Number` does not accept.
			let parsed = Number(String(value).replace(",", "."));

			return Number.isNaN(parsed) ? null : parsed;
		}
		default:
			return String(value);
	}
}

/**
 * The initial values for a set of parameters.
 *
 * Defaults naming a stored procedure are left unset: they have to be resolved
 * server-side, and the caller fills them in once they are.
 */
export function getDefaultParameterValues(parameters: ReportParameter[]): ReportParameterValues {
	let values: ReportParameterValues = {};

	for (let parameter of parameters) {
		values[parameter.name] = isProcedureDefault(parameter.defaultValue)
			? coerceParameterValue(parameter, null)
			: coerceParameterValue(parameter, parameter.defaultValue);
	}

	return values;
}

function toXmlValue(parameter: ReportParameter, value: ReportParameterValue): string {
	if (value === null || value === undefined) return "";
	// The Windows client writes booleans the way .NET does.
	if (typeof value === "boolean") return value ? "True" : "False";

	return String(value);
}

/**
 * Serializes parameter values the way the Windows filter builder does, so that
 * a filter saved on the web is readable in the Windows client and the other way
 * around.
 *
 * ```xml
 * <Parameters Version="1">
 *   <Parameter Name="Period" Type="Integer">202601</Parameter>
 * </Parameters>
 * ```
 */
export function serializeParameters(parameters: ReportParameter[], values: ReportParameterValues): string {
	let entries = parameters.map((parameter) => {
		let value = toXmlValue(parameter, values[parameter.name] ?? null);

		return `  <Parameter Name="${encodeXml(parameter.name)}" Type="${parameter.type}">${encodeXml(value)}</Parameter>`;
	});

	return `<Parameters Version="1">\n${entries.join("\n")}\n</Parameters>`;
}

/**
 * Reads the values back out of the XML stored on a saved filter. Values are
 * returned as strings and coerced against the report's own parameters by
 * `restoreParameterValues`, since the XML may name parameters the report no
 * longer has.
 */
export function parseParameters(xml: string | null | undefined): Record<string, string> {
	if (!xml) return {};

	let values: Record<string, string> = {};

	for (let match of xml.matchAll(/<Parameter\s+([^>]*?)\/?>(?:([\s\S]*?)<\/Parameter>)?/g)) {
		let name = /Name\s*=\s*"([^"]*)"/.exec(match[1])?.[1];
		if (!name) continue;

		values[name] = readElement(match[0], "Parameter") ?? "";
	}

	return values;
}

/**
 * Applies the values stored on a saved filter to a report's parameters, keeping
 * the defaults for anything the filter does not mention.
 */
export function restoreParameterValues(
	parameters: ReportParameter[],
	xml: string | null | undefined,
): ReportParameterValues {
	let stored = parseParameters(xml);
	let values = getDefaultParameterValues(parameters);

	for (let parameter of parameters) {
		if (parameter.name in stored) {
			values[parameter.name] = coerceParameterValue(parameter, stored[parameter.name]);
		}
	}

	return values;
}
